import { useTranslation } from 'react-i18next';

import { TGetEnabledFeaturesProps } from '@/shared/hooks/use-vendor/use-vendor.types';
import { useReadTenantQuery } from '@/shared/redux/api/ident/ident-api';
import {
  DeviceTypeEnum,
  FeatureConfigFragment,
  FeatureEnum,
  ProjectTypeEnum,
} from '@/shared/types/types.generated';

export const useVendor = () => {
  const { data, isLoading } = useReadTenantQuery();
  const vendors = data?.readTenant.vendors || [];

  const getVendor = (vendorId: string) => {
    return vendors.find((vendor) => vendor.vendorId === vendorId);
  };

  const getIDE = (vendorId?: string, ideConfigId?: string) => {
    if (!vendorId) {
      return undefined;
    }

    const vendor = getVendor(vendorId);
    return vendor?.ides?.find((ide) => ide.ideConfigId === ideConfigId);
  };

  const getProjectTypes = (vendorId: string, ideConfigId: string) => {
    const ide = getIDE(vendorId, ideConfigId);

    if (!ide?.fileTypes?.length) {
      return [];
    }

    const uniqueProjectTypes = new Set(
      ide.fileTypes.map((fileType) => fileType.projectType),
    );

    return Array.from(uniqueProjectTypes);
  };

  const getDeviceTypes = (vendorId: string, ideConfigId: string) => {
    const ide = getIDE(vendorId, ideConfigId);
    if (!ide?.devices?.length) {
      return [];
    }

    const uniqueDeviceTypes = new Set(
      ide.devices.map((device) => device.deviceType),
    );

    return Array.from(uniqueDeviceTypes);
  };

  const getDevice = (
    vendorId: string,
    ideConfigId: string,
    deviceType?: DeviceTypeEnum,
  ) => {
    const ide = getIDE(vendorId, ideConfigId);
    if (!ide?.devices?.length) {
      return;
    }

    return ide.devices.find((device) => device.deviceType === deviceType);
  };

  const getExtensionsForIde = (
    vendorId: string,
    ideConfigId: string,
    projectType?: ProjectTypeEnum,
  ) => {
    const ide = getIDE(vendorId, ideConfigId);

    if (!ide?.fileTypes?.length) {
      return [];
    }

    return ide.fileTypes
      .filter(
        (fileType) => !projectType || fileType.projectType === projectType,
      )
      .map((fileType) => fileType.extension);
  };

  const getEnabledFeatures = ({
    vendorId,
    ideConfigId,
    deviceType,
    projectType,
  }: TGetEnabledFeaturesProps) => {
    const ide = getIDE(vendorId, ideConfigId);
    const features = ide?.features || [];

    const deviceTypeFilter = (feature: FeatureConfigFragment) => {
      const { deviceTypes, fileExtensionsToDeviceTypes } = feature.metaData;

      const _deviceTypes = [
        ...(deviceTypes || []),
        ...(fileExtensionsToDeviceTypes?.map((item) => item.deviceType) || []),
      ];

      return _deviceTypes.includes(deviceType!);
    };

    const projectTypeFilter = (feature: FeatureConfigFragment) => {
      const {
        projectFileExtensions,
        fileExtensionsToDeviceTypes,
        fileToFileExtensions,
      } = feature.metaData;

      const _projectTypes = [
        ...(projectFileExtensions?.map((item) => item.projectType) || []),
        ...(fileExtensionsToDeviceTypes?.map((item) => item.projectType) || []),
        ...(fileToFileExtensions?.map((item) => item.projectType) || []),
      ];

      return _projectTypes.includes(projectType!);
    };

    if (deviceType) {
      return features.filter(deviceTypeFilter).map(({ feature }) => feature);
    }

    if (projectType) {
      return features.filter(projectTypeFilter).map(({ feature }) => feature);
    }

    return features.map(({ feature }) => feature);
  };

  const isIDEFeatureEnabled = (
    vendorId: string,
    ideConfigId: string,
    feature: FeatureEnum,
  ) => {
    const ide = getIDE(vendorId, ideConfigId);
    return (
      ide?.features?.some((_feature) => _feature.feature === feature) || false
    );
  };

  return {
    isLoading,
    vendors,
    getVendor,
    getIDE,
    getProjectTypes,
    getExtensionsForIde,
    isIDEFeatureEnabled,
    getDeviceTypes,
    getDevice,
    getEnabledFeatures,
  };
};

export const useVendorOptions = () => {
  const { t } = useTranslation();
  const {
    vendors,
    isLoading,
    getVendor,
    getProjectTypes,
    getDeviceTypes,
    getIDE,
  } = useVendor();

  const getVendorOptions = () => {
    if (!vendors) {
      return [];
    }

    return vendors.map((vendor) => {
      return {
        label: vendor.name,
        value: vendor.vendorId,
      };
    });
  };

  const getVendorIDEOptions = (vendorId: string) => {
    const ides = getVendor(vendorId)?.ides;

    if (!ides) {
      return [];
    }

    return ides.map((ide) => {
      return {
        label: ide.name,
        value: ide.ideConfigId,
        additionalInformation: ide.additionalInformation,
      };
    });
  };

  const getProjectTypesOptions = (vendorId: string, ideConfigId: string) => {
    const projectTypes = getProjectTypes(vendorId, ideConfigId);

    return projectTypes.map((projectType) => {
      return {
        label: t(
          `projects:form.fields.project_type.${projectType.toLowerCase()}`,
        ),
        value: projectType,
      };
    });
  };

  const getDeviceTypesOptions = (vendorId: string, ideConfigId: string) => {
    const deviceTypes = getDeviceTypes(vendorId, ideConfigId);

    return deviceTypes.map((deviceType) => {
      return {
        label: t(`device:device_types.${deviceType.toLowerCase()}`),
        value: deviceType,
      };
    });
  };

  const getProjectTypeFromFileExtensions = (
    vendorId: string,
    ideConfigId: string,
    fileExtension: string,
  ) => {
    const ide = getIDE(vendorId, ideConfigId);
    const fileTypes = ide?.fileTypes;

    if (!fileTypes) {
      return undefined;
    }

    return fileTypes.find(({ extension }) => extension === fileExtension)
      ?.projectType;
  };

  return {
    isLoading,
    getVendorOptions,
    getVendorIDEOptions,
    getProjectTypesOptions,
    getDeviceTypesOptions,
    getProjectTypeFromFileExtensions,
  };
};
